import type { CSSProperties } from 'react';
import { api } from '../../../lib/api/client.js';
import type { PracticeDay, PracticeDaysResponse } from '../../../lib/api/types.js';
import { useAsyncData } from '../../../ui/components/LoadingState/useAsyncData.ts';
import { Text } from '../../../ui/primitives/Text.tsx';
import { VisuallyHidden } from '../../../ui/primitives/VisuallyHidden.tsx';
import { weekdayInitial, weekdayName } from './weekday.ts';
import styles from './shared.module.css';

/**
 * The last seven days, as seven marks — lifted from `ProfilePage.tsx`'s
 * `PractisedWeek`, split into a presentational strip and the component that
 * fetches for it, so every direction can lay the same real behaviour out
 * differently without re-deriving it three times.
 *
 * See the original for the reasoning this keeps unchanged: the server sends
 * empty days rather than letting the strip invent them, the strip fills left
 * to right (§ 9 — the one direction progress is allowed to grow), the slot
 * holds its height before the answer arrives, and each mark's accessible name
 * carries the full day and count rather than leaving meaning in the glyph
 * alone (§ 12).
 */

export function WeekStrip({ days }: { days: readonly PracticeDay[] }) {
  const today = days[days.length - 1]?.date;

  return (
    <div className={styles.weekSlot}>
      {days.length > 0 ? (
        <ul className={styles.week} aria-label="Seus últimos sete dias">
          {days.map((day, index) => (
            <li
              key={day.date}
              className={styles.day}
              data-practised={day.recorded > 0 || undefined}
              data-today={day.date === today || undefined}
              style={{ '--day-index': index } as CSSProperties}
            >
              <span className={styles.dayVisual} aria-hidden="true">
                <span className={styles.dayMark} />
                <Text as="span" variant="caption" tone="muted">
                  {weekdayInitial(day.date)}
                </Text>
              </span>

              <VisuallyHidden>
                {weekdayName(day.date)}:{' '}
                {day.recorded === 0
                  ? 'nada praticado'
                  : `${day.recorded} ${day.recorded === 1 ? 'exercício' : 'exercícios'}`}
              </VisuallyHidden>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * `WeekStrip`, fetching for itself — the drop-in replacement for the inline
 * `PractisedWeek` the production page used to own directly.
 *
 * `fixtureDays` is a dev-only escape hatch: when a caller (the profile design
 * workbench, `dev/profile-exploration.tsx`) already has a week to show, no
 * request is made. Omit it and this fetches exactly as `PractisedWeek` did.
 */
export function PracticeWeekStrip({ fixtureDays }: { fixtureDays?: readonly PracticeDay[] }) {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { status, data } = useAsyncData<PracticeDaysResponse | null>(
    () =>
      fixtureDays
        ? Promise.resolve({ timeZone: zone, days: [...fixtureDays] })
        : api.practiceDays(zone),
    null,
    [zone, fixtureDays],
  );

  const days = status === 'ready' && data ? [...data.days].reverse() : [];
  return <WeekStrip days={days} />;
}
